import React from 'react';
import { Box, Container, Grid, Typography, Link as MuiLink, Divider, useTheme } from '@mui/material';
import LogoDarkFull from '@/asset/logo_dark_full_blue_accent_transparent.png';
import LogoLightFull from '@/asset/logo_light_full_transparent.png';

export const FooterSection: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const footerLogo = isDark ? LogoDarkFull : LogoLightFull;

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.secondary,
        pt: 10,
        pb: 4,
        borderTop: `1px solid ${theme.palette.divider}`,
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={6} sx={{ mb: 8 }}>
          {/* Brand Info, About & Address Container (Vertically centered) */}
          <Grid item xs={12} lg={7}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 3, sm: 5 },
              }}
            >
              {/* 4x Larger Logo */}
              <Box
                component="a"
                href="/"
                sx={{
                  display: 'inline-block',
                  flexShrink: 0,
                  textDecoration: 'none',
                }}
              >
                <Box
                  component="img"
                  src={footerLogo}
                  alt="Inverika Full Logo"
                  sx={{
                    height: { xs: 80, sm: 150, md: 190 },
                    maxWidth: { xs: '100%', sm: 280, md: 340 },
                    width: 'auto',
                    objectFit: 'contain',
                  }}
                />
              </Box>

              {/* Right Column: About Inverika + Registered Office (Vertically centered) */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.6,
                    fontSize: '0.9375rem',
                    mb: 2.5,
                  }}
                >
                  Inverika Enterprises Private Limited is a specialized contractor for facade systems, structural glazing, and infrastructure development projects across India.
                </Typography>

                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Registered Office:
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                    No.1, Akhitaan Complex, Behind Shirdi Sai Temple,<br />
                    AECI Layout, Kundalahalli, Bengaluru, Karnataka - 560037
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={6} lg={2.5}>
            <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontFamily: 'Space Grotesk', fontWeight: 600, mb: 3 }}>
              Quick Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                { label: 'About Company', href: '/about' },
                { label: 'Technical Capabilities', href: '/capabilities' },
                { label: 'Project Portfolio', href: '/projects' },
                { label: 'Industries We Serve', href: '/industries' },
                { label: 'Knowledge & Insights', href: '/insights' },
                { label: 'Careers & Hiring', href: '/careers' },
              ].map((item) => (
                <MuiLink
                  key={item.label}
                  href={item.href}
                  underline="hover"
                  sx={{ color: theme.palette.text.secondary, fontSize: '0.9375rem', '&:hover': { color: theme.palette.primary.main } }}
                >
                  {item.label}
                </MuiLink>
              ))}
            </Box>
          </Grid>

          {/* Compliance */}
          <Grid item xs={6} lg={2.5}>
            <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontFamily: 'Space Grotesk', fontWeight: 600, mb: 3 }}>
              Compliance
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {['Privacy Policy', 'Terms of Service', 'Safety Standards', 'Cookie Policy'].map((item) => (
                <MuiLink
                  key={item}
                  href="#"
                  underline="hover"
                  sx={{ color: theme.palette.text.secondary, fontSize: '0.9375rem', '&:hover': { color: theme.palette.primary.main } }}
                >
                  {item}
                </MuiLink>
              ))}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  border: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.palette.text.secondary,
                  cursor: 'pointer',
                  '&:hover': { borderColor: theme.palette.primary.main, color: theme.palette.primary.main },
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>public</span>
              </Box>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  border: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.palette.text.secondary,
                  cursor: 'pointer',
                  '&:hover': { borderColor: theme.palette.primary.main, color: theme.palette.primary.main },
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>group</span>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: theme.palette.divider, mb: 4 }} />

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            © {new Date().getFullYear()} Inverika Enterprises Private Limited. All Rights Reserved.
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            CIN: U41001KA2024PTC184411 | GSTIN: 29AAHCI5157L1Z0
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default FooterSection;
